import React from "react";
import { getCadence } from "lib/cadence";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Divider,
  Avatar,
  AvatarGroup,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useTheme,
  alpha,
  LinearProgress,
  keyframes,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useGetPayoutsQuery } from "components/Slices/circleApiSlice";
import { ImageAvatar } from "components/Circle/shared/ImageAvatar";
import dayjs from "dayjs";

// Icons
import CelebrationIcon from "@mui/icons-material/Celebration";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimelineIcon from "@mui/icons-material/Timeline";
import StarIcon from "@mui/icons-material/Star";

// Confetti animation
const confettiFloat = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) rotate(720deg);
    opacity: 0;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

interface GroupInfo {
  id: string;
  title: string;
  description: string;
  monthlyContribution: number;
  payoutAmount: number;
  currency: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  intervalUnit?: "day" | "week" | "month";
  intervalCount?: number;
}

interface PayoutMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayImg?: string;
  status: "Paid" | "Pending" | "Processing" | "Overdue" | "Delinquent";
  payoutDate: string;
  isPayee?: boolean;
}

interface CompletedPageProps {
  groupInfo: GroupInfo;
}

const CompletedPage: React.FC<CompletedPageProps> = ({ groupInfo }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { groupId } = useParams();

  const { data: payouts = [], isLoading } = useGetPayoutsQuery(groupId);

  // Calculate stats. The backend pot per round is contribution × (members − 1)
  // — the recipient doesn't pay into their own pot (circleController payoutAmount)
  // — so the circle-lifetime total is pot × rounds, not contribution × n².
  const totalMembers = payouts?.length || 0;
  const payoutPerPerson =
    groupInfo.payoutAmount ||
    groupInfo.monthlyContribution * Math.max(totalMembers - 1, 0);
  const totalMoneyCirculated = payoutPerPerson * totalMembers;

  // Calculate circle duration
  const firstPayout = payouts?.[0]?.payoutDate;
  const lastPayout = payouts?.[payouts.length - 1]?.payoutDate;
  const durationMonths =
    firstPayout && lastPayout
      ? dayjs(lastPayout).diff(dayjs(firstPayout), "month") + 1
      : totalMembers;

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: groupInfo.currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Confetti elements
  const confettiColors = [
    "#FFD700",
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
  ];
  const confettiElements = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 5}s`,
    duration: `${3 + Math.random() * 4}s`,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
    size: 8 + Math.random() * 8,
  }));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 50%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
        pb: 6,
      }}
    >
      {/* Confetti Animation */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {confettiElements.map((confetti) => (
          <Box
            key={confetti.id}
            sx={{
              position: "absolute",
              bottom: -20,
              left: confetti.left,
              width: confetti.size,
              height: confetti.size,
              backgroundColor: confetti.color,
              borderRadius: Math.random() > 0.5 ? "50%" : "2px",
              animation: `${confettiFloat} ${confetti.duration} ease-out ${confetti.delay} infinite`,
            }}
          />
        ))}
      </Box>

      {/* Main Content */}
      <Box sx={{ position: "relative", zIndex: 1, p: { xs: 2, sm: 4 } }}>
        {/* Hero Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            mb: 4,
            borderRadius: 4,
            textAlign: "center",
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
            color: "white",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(90deg, transparent, ${alpha("#fff", 0.1)}, transparent)`,
              animation: `${shimmer} 3s infinite`,
            },
          }}
        >
          <Box
            sx={{
              animation: `${pulse} 2s ease-in-out infinite`,
              display: "inline-block",
            }}
          >
            <EmojiEventsIcon
              sx={{
                fontSize: 80,
                mb: 2,
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
              }}
            />
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 1,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            Circle Complete
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              opacity: 0.95,
              mb: 2,
              fontSize: { xs: "1.1rem", sm: "1.5rem" },
            }}
          >
            {groupInfo.title}
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 600, mx: "auto", opacity: 0.9, lineHeight: 1.7 }}
          >
            Congratulations! Everyone in your circle has received their payout.
            Together you've helped each other achieve your financial goals.
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              icon: <AccountBalanceWalletIcon />,
              label: "Total Circulated",
              value: formatCurrency(totalMoneyCirculated),
              color: theme.palette.success.main,
              bgColor: alpha(theme.palette.success.main, 0.1),
            },
            {
              icon: <GroupsIcon />,
              label: "Circle Members",
              value: totalMembers.toString(),
              color: theme.palette.primary.main,
              bgColor: alpha(theme.palette.primary.main, 0.1),
            },
            {
              icon: <TrendingUpIcon />,
              label: "Payout Per Person",
              value: formatCurrency(payoutPerPerson),
              color: theme.palette.secondary.main,
              bgColor: alpha(theme.palette.secondary.main, 0.1),
            },
            {
              icon: <CalendarMonthIcon />,
              label: "Circle Duration",
              value: `${durationMonths} months`,
              color: theme.palette.info.main,
              bgColor: alpha(theme.palette.info.main, 0.1),
            },
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 25px ${alpha(stat.color, 0.25)}`,
                  },
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: stat.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      color: stat.color,
                    }}
                  >
                    {React.cloneElement(stat.icon, { sx: { fontSize: 28 } })}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: stat.color, mb: 0.5 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Circle Summary & Members */}
        <Grid container spacing={3}>
          {/* Circle Details */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <TimelineIcon color="primary" />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Circle Summary
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {groupInfo.description || "No description provided"}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {
                      getCadence(
                        groupInfo.intervalUnit,
                        groupInfo.intervalCount,
                      ).adjective
                    }{" "}
                    Contribution
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="primary.main"
                  >
                    {formatCurrency(groupInfo.monthlyContribution)}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Currency
                  </Typography>
                  <Chip
                    label={groupInfo.currency || "USD"}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Completion Status
                  </Typography>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Successfully Completed"
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Members
                  </Typography>
                  <AvatarGroup max={6} sx={{ justifyContent: "flex-start" }}>
                    {payouts?.map((member: PayoutMember) => (
                      <ImageAvatar
                        key={member.id}
                        displayImg={member.displayImg}
                        firstName={member.firstName}
                      />
                    ))}
                  </AvatarGroup>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Payout History Timeline */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <StarIcon sx={{ color: theme.palette.success.main }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Payout Timeline
                </Typography>
                <Chip
                  label={`${totalMembers} payouts`}
                  size="small"
                  sx={{ ml: "auto" }}
                  color="success"
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              {isLoading ? (
                <Box sx={{ py: 4 }}>
                  <LinearProgress />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, textAlign: "center" }}
                  >
                    Loading payout history...
                  </Typography>
                </Box>
              ) : (
                <List sx={{ py: 0, maxHeight: 400, overflow: "auto" }}>
                  {payouts?.map((member: PayoutMember, index: number) => (
                    <ListItem
                      key={member.id}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.04),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: theme.palette.success.main,
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <ListItemAvatar sx={{ minWidth: 48 }}>
                        <ImageAvatar
                          displayImg={member.displayImg}
                          firstName={member.firstName}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight={600}>
                            {member.firstName} {member.lastName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {member.email}
                          </Typography>
                        }
                      />
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="success.main"
                        >
                          {formatCurrency(payoutPerPerson)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(member.payoutDate).format("MMM D, YYYY")}
                        </Typography>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Achievement Banner */}
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
            border: `2px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
            textAlign: "center",
          }}
        >
          <CelebrationIcon
            sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }}
          />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            A Journey of Trust & Community
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", mb: 3 }}
          >
            Your circle has successfully completed {durationMonths} months of
            collective saving and sharing. Together, you circulated{" "}
            <strong style={{ color: theme.palette.success.main }}>
              {formatCurrency(totalMoneyCirculated)}
            </strong>{" "}
            and helped {totalMembers} members achieve their goals.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Chip
              icon={<CheckCircleIcon />}
              label="100% Completion Rate"
              color="success"
              sx={{ px: 1 }}
            />
            <Chip
              icon={<GroupsIcon />}
              label={`${totalMembers} Happy Members`}
              color="primary"
              sx={{ px: 1 }}
            />
            <Chip
              icon={<EmojiEventsIcon />}
              label="Circle Champion"
              sx={{
                px: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.2),
                color: theme.palette.warning.dark,
                "& .MuiChip-icon": { color: theme.palette.warning.dark },
              }}
            />
          </Box>
        </Paper>

        {/* Call to Action */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate("/create-group")}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
              fontSize: "1rem",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              "&:hover": {
                boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
            }}
          >
            Start a New Circle
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/")}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
              fontSize: "1rem",
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
              },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CompletedPage;
